import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { AccountRow } from '../../../../shared/src/types';
import { API_BASE_URL } from '../config';

export default function ChannelsPage() {
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const connectedParam = searchParams.get('connected');
  const countParam = searchParams.get('count');

  const facebookConnectUrl = `${API_BASE_URL}/api/auth/facebook/start?redirect_to=${encodeURIComponent(
    typeof window !== 'undefined' ? `${window.location.origin}/channels` : '/channels'
  )}`;

  const hasTikTok = accounts.some(a => a.provider === 'tiktok');

  const tiktokConnectUrl = `${API_BASE_URL}/api/auth/tiktok/start?redirect_to=${encodeURIComponent(
    typeof window !== 'undefined' ? `${window.location.origin}/channels` : '/channels'
  )}${hasTikTok ? '&force=true' : ''}`;

  const fetchAccounts = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/accounts`);
      if (!res.ok) {
        let msg = `Failed to load accounts (status ${res.status})`;
        try {
          const errData = (await res.json()) as { error?: string };
          if (errData?.error) msg = `Failed to load accounts: ${errData.error}`;
        } catch {}
        throw new Error(msg);
      }
      const data = (await res.json()) as { accounts: AccountRow[] };
      setAccounts(data.accounts || []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const handleDisconnect = async (id: string, pageName: string | null) => {
    const confirmed = window.confirm(
      `Are you sure you want to disconnect "${pageName || 'this channel'}"?`
    );
    if (!confirmed) return;

    setDisconnectingId(id);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/accounts/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        let msg = `Failed to disconnect (status ${res.status})`;
        try {
          const errData = (await res.json()) as { error?: string };
          if (errData?.error) msg = `Failed to disconnect: ${errData.error}`;
        } catch {}
        throw new Error(msg);
      }
      setAccounts((prev) => prev.filter((acc) => acc.id !== id));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to disconnect channel';
      setError(msg);
    } finally {
      setDisconnectingId(null);
    }
  };

  const dismissBanner = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('connected');
    nextParams.delete('count');
    setSearchParams(nextParams, { replace: true });
  };

  return (
    <div style={{ maxWidth: 840, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 600 }}>Channels</h1>
          <p style={{ margin: '0.25rem 0 0', color: '#666', fontSize: '0.95rem' }}>
            Connect and manage social accounts for video publishing.
          </p>
        </div>

        {accounts.length > 0 && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <a
              href={facebookConnectUrl}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                backgroundColor: '#1877F2',
                color: '#fff',
                padding: '0.6rem 1.1rem',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: 500,
                fontSize: '0.9rem',
              }}
            >
              + Facebook
            </a>
            <a
              href={tiktokConnectUrl}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                backgroundColor: '#000000',
                color: '#fff',
                padding: '0.6rem 1.1rem',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: 500,
                fontSize: '0.9rem',
              }}
            >
              {hasTikTok ? '+ Connect another TikTok' : '+ Connect TikTok'}
            </a>
          </div>
        )}
      </div>

      {/* Success Banner */}
      {connectedParam && (
        <div
          style={{
            backgroundColor: '#e8f5e9',
            border: '1px solid #c8e6c9',
            color: '#2e7d32',
            padding: '0.85rem 1.25rem',
            borderRadius: '6px',
            marginBottom: '1.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>
            <strong>Success!</strong> Account connected ({countParam || '1'} page(s) loaded).
          </span>
          <button
            onClick={dismissBanner}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#2e7d32',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '1rem',
              padding: '0.25rem 0.5rem',
            }}
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div
          style={{
            backgroundColor: '#ffebee',
            border: '1px solid #ffcdd2',
            color: '#c62828',
            padding: '0.85rem 1.25rem',
            borderRadius: '6px',
            marginBottom: '1.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>
            <strong>Error:</strong> {error}
          </span>
          <button
            onClick={fetchAccounts}
            style={{
              backgroundColor: '#c62828',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              padding: '0.35rem 0.75rem',
              cursor: 'pointer',
              fontSize: '0.85rem',
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div
          style={{
            textAlign: 'center',
            padding: '3rem 1rem',
            color: '#777',
            backgroundColor: '#fafafa',
            borderRadius: '8px',
            border: '1px solid #eee',
          }}
        >
          <div style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>Loading connected channels...</div>
          <p style={{ margin: 0, fontSize: '0.9rem', color: '#999' }}>Fetching accounts from API</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && accounts.length === 0 && !error && (
        <div
          style={{
            textAlign: 'center',
            padding: '3.5rem 1.5rem',
            backgroundColor: '#fcfcfc',
            border: '1px dashed #ccc',
            borderRadius: '8px',
          }}
        >
          <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🔌</div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: '0 0 0.5rem' }}>
            No Channels Connected
          </h2>
          <p style={{ color: '#666', maxWidth: 440, margin: '0 auto 1.5rem', fontSize: '0.95rem' }}>
            Connect a social account to publish and crosspost videos.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
            <a
              href={facebookConnectUrl}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                backgroundColor: '#1877F2',
                color: '#fff',
                padding: '0.75rem 1.4rem',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: 500,
                fontSize: '0.95rem',
                boxShadow: '0 2px 4px rgba(24, 119, 242, 0.2)',
              }}
            >
              Connect Facebook
            </a>
            <a
              href={tiktokConnectUrl}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                backgroundColor: '#000000',
                color: '#fff',
                padding: '0.75rem 1.4rem',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: 500,
                fontSize: '0.95rem',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
              }}
            >
              {hasTikTok ? 'Connect another TikTok' : 'Connect TikTok'}
            </a>
          </div>
        </div>
      )}

      {/* Connected Accounts List */}
      {!loading && accounts.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {accounts.map((account) => {
              const isTikTok = account.provider === 'tiktok';
              const displayName = isTikTok
                ? (account.display_name || 'TikTok User')
                : (account.page_name || 'Unnamed Page');
              const pictureUrl = isTikTok ? account.avatar_url : account.page_picture;
              const subline = isTikTok
                ? (account.username ? `@${account.username}` : 'TikTok')
                : `${account.page_username ? `@${account.page_username} • ` : ''}Page ID: ${account.page_id || 'N/A'}`;
              const avatarBg = isTikTok ? '#000' : '#e3f2fd';
              const avatarColor = isTikTok ? '#fff' : '#1877F2';

              return (
                <div
                  key={account.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '1.1rem 1.25rem',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    gap: '1rem',
                    flexWrap: 'wrap',
                  }}
                >
                  {/* Account Info */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', minWidth: 220 }}>
                    {pictureUrl ? (
                      <img
                        src={pictureUrl}
                        alt={displayName}
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          objectFit: 'cover',
                          border: '1px solid #ddd',
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          backgroundColor: avatarBg,
                          color: avatarColor,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 600,
                          fontSize: '1.2rem',
                        }}
                      >
                        {displayName.charAt(0).toUpperCase()}
                      </div>
                    )}

                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontWeight: 600, fontSize: '1.05rem', color: '#111' }}>
                          {displayName}
                        </span>
                        <span
                          style={{
                            backgroundColor: '#e8f5e9',
                            color: '#2e7d32',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            padding: '0.15rem 0.5rem',
                            borderRadius: '12px',
                          }}
                        >
                          Connected
                        </span>
                      </div>

                      <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.2rem' }}>
                        {subline}
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <button
                      onClick={() => handleDisconnect(account.id, displayName)}
                      disabled={disconnectingId === account.id}
                      style={{
                        backgroundColor: '#fff',
                        color: '#d32f2f',
                        border: '1px solid #ef9a9a',
                        padding: '0.5rem 0.9rem',
                        borderRadius: '6px',
                        cursor: disconnectingId === account.id ? 'not-allowed' : 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: 500,
                        opacity: disconnectingId === account.id ? 0.6 : 1,
                        transition: 'all 0.15s ease',
                      }}
                      onMouseEnter={(e) => {
                        if (disconnectingId !== account.id) {
                          e.currentTarget.style.backgroundColor = '#ffebee';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#fff';
                      }}
                    >
                      {disconnectingId === account.id ? 'Disconnecting...' : 'Disconnect'}
                    </button>
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}